/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, ChangeDetectorRef, Component, Injector, Input } from '@angular/core';
import {
    type ColumnDef,
    type TableConfig,
    CAPACITY_UNIT,
    I18nService,
    TableAbstract,
    UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { ToolService } from 'app/common/services';
import { type Dtree, type FileSystem, type Quota } from 'app/common/types/mo';
import {
    QueryQuotaTypeE,
    UserQuotaStrService,
} from 'app/feature/service/file-service/quota/user-quota/user-quota-str.service';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { InfigoService } from '../../../infigo.service';

@Component({
    selector: 'app-file-quota-table',
    templateUrl: './file-quota-table.component.html',
})
export class FileQuotaTableComponent extends TableAbstract<Quota> implements OnChanges {
    @Input() dtreeData: Dtree;
    @Input() fsData: FileSystem;
    @Input() checkOnlyShowFsQuota;
    @Input() loading = false;
    $destroy = new Subject();
    quotaData: Quota[] = [];
    // 表格行配置对象
    tableColumnDefs: ColumnDef[] = [];
    // 表格总配置对象
    tableConfig: TableConfig;
    tableColumnDefsConst: ColumnDef[] = [
        {
            field: 'ID',
            header: this.i18n.get('common.ID_column'),
            hidden: true,
        },
        {
            field: 'QUOTATYPE',
            header: this.i18n.get('service.quota_term_quotaType_lab'),
            render: (value: string): string => this.str.quotaTypeI18n[value],
        },
        {
            field: 'USRGRPOWNERNAME',
            header: this.i18n.get('service.quota_term_user_lab'),
            render: this.str.userRender,
            className: ['table-cell-text-pre'],
        },
        {
            field: 'USRGRPOWNERNAME',
            header: this.i18n.get('service.quota_term_userGroup_lab'),
            render: this.str.groupRender,
            className: ['table-cell-text-pre'],
        },
        {
            field: 'USRGRPTYPE',
            header: this.i18n.get('service.quota_term_userorGroupType_lab'),
            render: this.str.userOrGroupTypeRender,
        },
        {
            field: 'SPACEHARDQUOTA',
            header: this.i18n.get('service.quota_term_view_spaceQuota_column'),
            render: (vale: string, col, data: Quota): string => {
                // 软 / 硬
                const hard = this.toolService.isInvalidNum(data.SPACEHARDQUOTA, true) ?
                    UNPLACEHOLDER : this.utilService.adaptUnit(data.SPACEHARDQUOTA, CAPACITY_UNIT.KB).label;

                const soft = this.toolService.isInvalidNum(data.SPACESOFTQUOTA, true) ?
                    UNPLACEHOLDER : this.utilService.adaptUnit(data.SPACESOFTQUOTA, CAPACITY_UNIT.KB).label;

                return soft + '/' + hard;
            },
        },
        {
            field: 'SPACEUSED',
            header: this.i18n.get('service.quota_term_create_spaceUsedQuota_column'),
            render: (value: string): string => this.toolService.isInvalidNum(value, true) ?
                UNPLACEHOLDER :
                this.utilService.adaptUnit(value, CAPACITY_UNIT.KB).label,
        },
        {
            field: 'SPACEUSEDRATE',
            header: this.i18n.get('service.quota_term_create_spaceQuotaUsage_column') + this.i18n.get('common.common_unit_lab', ['%']),
            render: (value: string): string => this.toolService.isInvalidNum(value) ? UNPLACEHOLDER : value,
        },
        {
            field: 'FILEHARDQUOTA',
            header: this.i18n.get('service.quota_term_view_fileNumberQuota_column'),
            render: (vale: string, col, data: Quota): string => {
                // 软 / 硬
                const hard = this.toolService.isInvalidNum(data.FILEHARDQUOTA, true) ? UNPLACEHOLDER : data.FILEHARDQUOTA;

                const soft = this.toolService.isInvalidNum(data.FILESOFTQUOTA, true) ? UNPLACEHOLDER : data.FILESOFTQUOTA;

                return soft + '/' + hard;
            },
        },
        {
            field: 'FILEUSED',
            header: this.i18n.get('service.quota_term_create_fileNumberUsedQuota_column'),
            render: (value: string): string => this.toolService.isInvalidNum(value, true) ? UNPLACEHOLDER : value,
        },
        {
            field: 'FILEUSEDRATE',
            header: this.i18n.get('service.quota_term_create_fileNumberQuotaUsage_column') + this.i18n.get('common.common_unit_lab', ['%']),
            render: (value: string): string => this.toolService.isInvalidNum(value) ? UNPLACEHOLDER : value,
        },
    ];

    constructor(
        private i18n: I18nService,
        public str: UserQuotaStrService,
        private utilService: UtilService,
        private toolService: ToolService,
        public infigoService: InfigoService,
        private changeDetectorRef: ChangeDetectorRef,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(): void {
        this.addOrRemoveDtree(this.checkOnlyShowFsQuota);
        if (this.dtreeData || this.fsData) {
            this.getQuotaData(this.dtreeData.ID ? this.dtreeData : this.fsData);
        }
    }

    initBtns(): void {
    }

    initTable(): void {
        this.tableColumnDefs = [...this.tableColumnDefsConst];
        this.tableConfig = {
            dataKey: 'ID',
            motion: false,
            rows: 5,
        };
        this.addOrRemoveDtree(this.checkOnlyShowFsQuota);
    }

    getQuotaData(data: Dtree | FileSystem): void {
        this.loading = true;
        this.infigoService.getQuotaList(data.TYPE, data.ID, QueryQuotaTypeE.Report, data.vstoreId)
            .pipe(takeUntil(this.$destroy), catchError((errorData) => of(errorData)))
            .subscribe((value) => {
                this.loading = false;
                this.quotaData = value.data;
                this.changeDetectorRef.detectChanges();
            }, () => {
                this.quotaData = [];
                this.loading = false;
            });
    }

    addOrRemoveDtree(isAdd: boolean): void {
        const haveDtree = this.tableColumnDefs.some((item) => item.field === 'RESUSENAME');
        if (!isAdd && !haveDtree) {
            // 触发data-table ngOnChange属性更新
            const dtreeColumObj: ColumnDef = {
                field: 'RESUSENAME',
                header: this.i18n.get('service.quota_term_create_Dtree_lab'),
                filterType: 'search',
                filterTrim: false,
                ignore: this.checkOnlyShowFsQuota,
                render: this.str.resUseNameRender,
                className: ['table-cell-text-pre'],
            };
            this.tableColumnDefs = [dtreeColumObj, ...this.tableColumnDefsConst];
        }
        if (isAdd && haveDtree) {
            // 触发data-table ngOnChange属性更新
            this.tableColumnDefs = [...this.tableColumnDefsConst];
        }
    }
}
